/**
 * SNS 공유 기능을 위한 유틸리티 라이브러리
 */

import {
  measureSharePerformance,
  measureClipboardPerformance,
} from "~/lib/performance-monitor";

export interface ShareContent {
  title: string;
  description: string;
  url: string;
  imageUrl?: string;
  hashtags?: string[];
}

export type SharePlatform =
  | "facebook"
  | "twitter"
  | "instagram"
  | "clipboard"
  | "native";

/**
 * Facebook 공유 URL 생성
 */
export function createFacebookShareUrl(content: ShareContent): string {
  const params = new URLSearchParams({
    u: content.url,
    quote: `${content.title}\n\n${content.description}`,
  });

  return `https://www.facebook.com/sharer/sharer.php?${params.toString()}`;
}

/**
 * X (Twitter) 공유 URL 생성
 */
export function createTwitterShareUrl(content: ShareContent): string {
  const hashtags = content.hashtags?.join(",") || "";
  const text = `${content.title}\n\n${content.description}`;

  const params = new URLSearchParams({
    text,
    url: content.url,
    ...(hashtags && { hashtags }),
  });

  return `https://twitter.com/intent/tweet?${params.toString()}`;
}

/**
 * Instagram용 클립보드 텍스트 생성
 * Instagram은 직접 링크 공유를 지원하지 않으므로 클립보드 복사 방식 사용
 */
export function createInstagramShareText(content: ShareContent): string {
  const hashtags = content.hashtags?.map((tag) => `#${tag}`).join(" ") || "";

  // 아티클인지 레시피인지 구분하여 다른 형식 적용
  const isArticle = content.url.includes("/articles/");
  const baseHashtags = isArticle
    ? "#슬로우다이브 #건강정보 #웰빙"
    : "#슬로우다이브 #저속노화 #건강레시피";

  return `${content.title}

${content.description}

더 자세한 내용은 링크에서 확인하세요 👆
${content.url}

${hashtags}

${baseHashtags}`;
}

/**
 * 클립보드 복사용 텍스트 생성
 */
export function createClipboardText(content: ShareContent): string {
  return `${content.title}

${content.description}

${content.url}`;
}

/**
 * Web Share API를 사용한 네이티브 공유 (모바일 UX 최적화)
 */
export async function shareViaWebAPI(content: ShareContent): Promise<boolean> {
  if (!navigator.share) {
    return false;
  }

  try {
    // Web Share API 지원 데이터 확인
    const shareData: ShareData = {
      title: content.title,
      text: content.description,
      url: content.url,
    };

    // 이미지 공유 지원 확인 (일부 브라우저에서 지원)
    if (content.imageUrl && navigator.canShare) {
      try {
        const response = await fetch(content.imageUrl);
        const blob = await response.blob();
        const file = new File([blob], "image.jpg", { type: blob.type });

        if (navigator.canShare({ files: [file] })) {
          shareData.files = [file];
        }
      } catch (imageError) {
        // 이미지 로드 실패 시 텍스트만 공유
        console.log("이미지 공유 실패, 텍스트만 공유:", imageError);
      }
    }

    await navigator.share(shareData);
    return true;
  } catch (error) {
    // AbortError는 사용자가 공유를 취소한 경우
    if (error instanceof Error && error.name === "AbortError") {
      console.log("사용자가 공유를 취소했습니다");
      return false;
    }

    console.error("Web Share API 오류:", error);
    return false;
  }
}

/**
 * 클립보드에 텍스트 복사 (성능 최적화 및 모바일 UX 개선)
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false;

  const startTime = performance.now();

  try {
    // 모던 브라우저의 Clipboard API 사용
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      measureClipboardPerformance(startTime);
      return true;
    } else {
      // 모바일 및 구형 브라우저를 위한 fallback
      const result = await fallbackCopyToClipboard(text);
      measureClipboardPerformance(startTime);
      return result;
    }
  } catch (error) {
    console.error("클립보드 복사 실패:", error);
    // Clipboard API 실패 시 fallback 시도
    const result = await fallbackCopyToClipboard(text);
    measureClipboardPerformance(startTime);
    return result;
  }
}

/**
 * 클립보드 복사 fallback (모바일 최적화)
 */
async function fallbackCopyToClipboard(text: string): Promise<boolean> {
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // 모바일에서 가상 키보드가 나타나지 않도록 설정
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "-9999px";
    textArea.style.opacity = "0";
    textArea.style.pointerEvents = "none";
    textArea.setAttribute("readonly", "");
    textArea.setAttribute("contenteditable", "true");

    document.body.appendChild(textArea);

    // iOS Safari 지원
    if (navigator.userAgent.match(/ipad|iphone/i)) {
      const range = document.createRange();
      range.selectNodeContents(textArea);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      textArea.setSelectionRange(0, 999999);
    } else {
      textArea.select();
      textArea.setSelectionRange(0, 999999);
    }

    const success = document.execCommand("copy");
    document.body.removeChild(textArea);
    return success;
  } catch (error) {
    console.error("Fallback 클립보드 복사 실패:", error);
    return false;
  }
}

/**
 * 플랫폼별 공유 실행 (성능 최적화 및 모바일 UX 개선)
 */
export async function shareContent(
  platform: SharePlatform,
  content: ShareContent
): Promise<{ success: boolean; message?: string }> {
  const startTime = performance.now();
  // 입력 검증
  if (!content.title || !content.url) {
    return { success: false, message: "공유할 콘텐츠 정보가 부족합니다." };
  }

  try {
    switch (platform) {
      case "facebook": {
        const url = createFacebookShareUrl(content);
        const opened = openShareWindow(url, "Facebook 공유", 600, 400);
        return {
          success: opened,
          message: opened
            ? "Facebook 공유 창이 열렸습니다."
            : "팝업이 차단되었습니다. 팝업 차단을 해제해주세요.",
        };
      }

      case "twitter": {
        const url = createTwitterShareUrl(content);
        const opened = openShareWindow(url, "X 공유", 600, 400);
        return {
          success: opened,
          message: opened
            ? "X 공유 창이 열렸습니다."
            : "팝업이 차단되었습니다. 팝업 차단을 해제해주세요.",
        };
      }

      case "instagram": {
        const text = createInstagramShareText(content);
        const success = await copyToClipboard(text);
        return {
          success,
          message: success
            ? "Instagram용 텍스트가 클립보드에 복사되었습니다. Instagram 앱에서 붙여넣기 하세요."
            : "클립보드 복사에 실패했습니다. 브라우저 설정을 확인해주세요.",
        };
      }

      case "clipboard": {
        const text = createClipboardText(content);
        const success = await copyToClipboard(text);
        return {
          success,
          message: success
            ? "링크가 클립보드에 복사되었습니다."
            : "클립보드 복사에 실패했습니다. 브라우저 설정을 확인해주세요.",
        };
      }

      case "native": {
        const success = await shareViaWebAPI(content);
        return {
          success,
          message: success
            ? "공유가 완료되었습니다."
            : "기기의 공유 기능을 사용할 수 없습니다.",
        };
      }

      default:
        return { success: false, message: "지원하지 않는 플랫폼입니다." };
    }
  } catch (error) {
    console.error("공유 실패:", error);
    measureSharePerformance(platform, startTime);
    return { success: false, message: "공유 중 오류가 발생했습니다." };
  }
}

/**
 * 공유 창 열기 (팝업 차단 감지 포함)
 */
function openShareWindow(
  url: string,
  title: string,
  width: number,
  height: number
): boolean {
  try {
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const features = `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`;
    const popup = window.open(url, title, features);

    // 팝업이 차단되었는지 확인
    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      return false;
    }

    // 포커스 설정
    popup.focus();
    return true;
  } catch (error) {
    console.error("공유 창 열기 실패:", error);
    return false;
  }
}

/**
 * 레시피 공유 콘텐츠 생성
 */
export function createRecipeShareContent(recipe: {
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  category?: string;
}): ShareContent {
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${baseUrl}/recipes/${recipe.id}`;

  const hashtags = ["슬로우다이브", "저속노화", "건강레시피"];
  if (recipe.category) {
    hashtags.push(recipe.category);
  }

  return {
    title: `${recipe.title} | 슬로우다이브`,
    description: recipe.description || "건강한 저속노화 레시피를 확인해보세요!",
    url,
    imageUrl: recipe.imageUrl,
    hashtags,
  };
}

/**
 * 아티클 공유 콘텐츠 생성
 */
export function createArticleShareContent(article: {
  slug: string;
  title: string;
  excerpt?: string | null;
  category?: string;
  featuredImageUrl?: string | null;
}): ShareContent {
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${baseUrl}/articles/${article.slug}`;

  const hashtags = ["슬로우다이브", "건강정보"];

  // 카테고리별 최적화된 해시태그 추가
  if (article.category) {
    const categoryHashtags: Record<string, string[]> = {
      "slow-aging": ["저속노화", "안티에이징", "건강한노화", "장수비결"],
      "health-info": ["건강정보", "건강관리", "웰빙", "건강팁"],
      ingredients: ["식재료정보", "건강식품", "슈퍼푸드", "영양성분"],
      nutrition: ["영양정보", "영양학", "균형식단", "건강식단"],
      lifestyle: ["라이프스타일", "건강한생활", "웰니스", "생활습관"],
    };

    const categoryTags = categoryHashtags[article.category] || [
      article.category,
    ];
    hashtags.push(...categoryTags);
  }

  return {
    title: `${article.title} | 슬로우다이브`,
    description: article.excerpt || "유용한 건강 정보를 확인해보세요!",
    url,
    imageUrl: article.featuredImageUrl || undefined,
    hashtags,
  };
}

/**
 * Web Share API 지원 여부 확인 (캐시 적용)
 */
let webShareSupported: boolean | null = null;
export function isWebShareSupported(): boolean {
  if (webShareSupported !== null) {
    return webShareSupported;
  }

  webShareSupported = typeof navigator !== "undefined" && "share" in navigator;
  return webShareSupported;
}

/**
 * 클립보드 API 지원 여부 확인 (캐시 적용)
 */
let clipboardSupported: boolean | null = null;
export function isClipboardSupported(): boolean {
  if (clipboardSupported !== null) {
    return clipboardSupported;
  }

  clipboardSupported =
    typeof navigator !== "undefined" &&
    ("clipboard" in navigator || document.queryCommandSupported?.("copy"));
  return clipboardSupported;
}

/**
 * 모바일 디바이스 감지
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * 터치 디바이스 감지
 */
export function isTouchDevice(): boolean {
  if (typeof window === "undefined") return false;

  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}
